import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Stethoscope, Activity, ClipboardList, HeartPulse } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SITE, waLink } from "@/lib/site";
import { GUIDE_SECTIONS } from "@/data/content";

const GUIDE_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_page-from-doc-6/artifacts/6sv6min3_IMG_3128.webp";

function ListBlock({ title, items }) {
  return (
    <div>
      <h4 className="text-xs tracking-[0.18em] uppercase text-brand-pine mb-4">{title}</h4>
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-3 text-brand-ink">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-pine shrink-0" />
            <span className="text-[15px] leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TreatmentGuide() {
  const [active, setActive] = useState(GUIDE_SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    GUIDE_SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: "ENT Treatment Guide — Varanasi",
      about: GUIDE_SECTIONS.map((s) => ({ "@type": "MedicalCondition", name: s.title })),
      author: { "@id": "https://entspecialistvaranasi.com/#doctor" },
      isPartOf: { "@id": "https://entspecialistvaranasi.com/#website" },
    },
    breadcrumbSchema([
      { name: "Home", url: "https://entspecialistvaranasi.com/" },
      { name: "ENT Treatment Guide", url: "https://entspecialistvaranasi.com/ent-treatment-guide-varanasi" },
    ]),
  ];

  return (
    <>
      <Seo
        title="ENT Treatment Guide in Varanasi — Ear, Nose & Throat | Dr. Sanjay Teza"
        description="A detailed ENT treatment guide for Varanasi covering ear, nose and throat disorders and advanced procedures — overview, symptoms, causes, diagnosis, treatment and recovery."
        path="/ent-treatment-guide-varanasi"
        schemas={schemas}
      />

      {/* Header */}
      <section className="pt-36 md:pt-44 pb-16 border-b border-brand-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <nav className="flex items-center gap-2 text-xs text-brand-muted mb-8" data-testid="guide-breadcrumb">
                <Link to="/" className="hover:text-brand-pine">Home</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-brand-ink">Treatment Guide</span>
              </nav>
              <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">Clinical Guide</p>
              <h1 className="font-heading font-bold text-brand-ink text-5xl md:text-6xl lg:text-7xl leading-[1.03] tracking-tight">
                ENT Treatment Guide
              </h1>
              <p className="mt-6 text-lg text-brand-muted max-w-xl leading-relaxed">
                Understand how {SITE.doctorName} diagnoses and treats ear, nose and throat conditions
                in Varanasi — from first symptoms to full recovery.
              </p>
            </Reveal>
          </div>
          <Reveal delay={1} className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl aspect-[16/10]">
              <img src={GUIDE_IMG} alt="ENT clinical illustration" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Content with sticky TOC */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs tracking-[0.18em] uppercase text-brand-muted mb-5">On this page</p>
              <nav className="flex flex-col gap-1" data-testid="guide-toc">
                {GUIDE_SECTIONS.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    data-testid={`guide-toc-${s.id}`}
                    className={`py-2.5 pl-4 border-l-2 text-sm transition-colors duration-200 ${
                      active === s.id
                        ? "border-brand-pine text-brand-pine"
                        : "border-brand-line text-brand-muted hover:text-brand-ink"
                    }`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="lg:col-span-9 space-y-24">
            {GUIDE_SECTIONS.map((s) => (
              <article key={s.id} id={s.id} className="scroll-mt-28" data-testid={`guide-section-${s.id}`}>
                <Reveal>
                  <h2 className="font-heading font-bold text-brand-ink text-3xl md:text-4xl tracking-tight">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-lg text-brand-muted leading-relaxed max-w-2xl">{s.lead}</p>
                </Reveal>

                <Reveal delay={1} className="mt-8">
                  <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="flex flex-wrap h-auto bg-brand-mint/25 p-1.5 rounded-full gap-1">
                      {[
                        { v: "overview", l: "Overview" },
                        { v: "symptoms", l: "Symptoms" },
                        { v: "causes", l: "Causes" },
                        { v: "diagnosis", l: "Diagnosis" },
                        { v: "options", l: "Treatment" },
                        { v: "recovery", l: "Recovery" },
                      ].map((t) => (
                        <TabsTrigger
                          key={t.v}
                          value={t.v}
                          data-testid={`guide-tab-${s.id}-${t.v}`}
                          className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-brand-teal data-[state=active]:text-brand-ink text-brand-ink"
                        >
                          {t.l}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    <div className="mt-8 bg-brand-surface border border-brand-line rounded-2xl p-8 md:p-10">
                      <TabsContent value="overview" className="mt-0">
                        <div className="flex items-start gap-4">
                          <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-mint/50 text-brand-pine shrink-0">
                            <ClipboardList className="w-5 h-5" />
                          </span>
                          <p className="text-[15px] md:text-base leading-relaxed text-brand-ink">{s.overview}</p>
                        </div>
                      </TabsContent>
                      <TabsContent value="symptoms" className="mt-0">
                        <ListBlock title="Common symptoms" items={s.symptoms} />
                      </TabsContent>
                      <TabsContent value="causes" className="mt-0">
                        <ListBlock title="Possible causes" items={s.causes} />
                      </TabsContent>
                      <TabsContent value="diagnosis" className="mt-0">
                        <div className="flex items-start gap-4 mb-6 text-brand-pine">
                          <Stethoscope className="w-5 h-5" />
                          <span className="text-xs tracking-[0.18em] uppercase pt-0.5">How we diagnose</span>
                        </div>
                        <ListBlock title="Diagnostic steps" items={s.diagnosis} />
                      </TabsContent>
                      <TabsContent value="options" className="mt-0">
                        <div className="flex items-start gap-4 mb-6 text-brand-pine">
                          <Activity className="w-5 h-5" />
                          <span className="text-xs tracking-[0.18em] uppercase pt-0.5">Treatment options</span>
                        </div>
                        <ListBlock title="Available treatments" items={s.options} />
                      </TabsContent>
                      <TabsContent value="recovery" className="mt-0">
                        <div className="flex items-start gap-4">
                          <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-mint/50 text-brand-pine shrink-0">
                            <HeartPulse className="w-5 h-5" />
                          </span>
                          <p className="text-[15px] md:text-base leading-relaxed text-brand-ink">{s.recovery}</p>
                        </div>
                      </TabsContent>
                    </div>
                  </Tabs>
                </Reveal>
              </article>
            ))}

            {/* Inline CTA */}
            <div className="rounded-3xl bg-brand-ink text-brand-bg p-10 md:p-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl">Not sure which applies to you?</h2>
              <p className="mt-4 text-brand-bg/60 max-w-lg">
                Book a consultation and get an accurate diagnosis and a treatment plan tailored to your needs.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={waLink("Hello, I would like to discuss an ENT treatment.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="guide-cta-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-7 py-3.5 text-sm text-white transition-colors hover:bg-whatsapp-hover"
                >
                  Book on WhatsApp
                </a>
                <Link
                  to="/contact"
                  data-testid="guide-cta-contact-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-bg/25 px-7 py-3.5 text-sm text-brand-bg transition-colors hover:border-brand-mint hover:text-brand-mint"
                >
                  Contact the clinic
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
